/**
 * Form state management with Zustand
 */

import { create } from "zustand";
import { FormSchema, IMO, Carrier } from "@aam/shared";

interface FormStoreState {
  // Current form state
  currentFormSchema: FormSchema | null;
  currentIMO: IMO | null;
  currentCarrier: Carrier | null;
  formData: Record<string, any>;
  encryptedFields: string[];

  // UI state
  currentStep: number;
  isSubmitting: boolean;
  error: string | null;
  success: boolean;

  // Actions
  setFormSchema: (schema: FormSchema) => void;
  setIMO: (imo: IMO) => void;
  setCarrier: (carrier: Carrier) => void;
  updateFormData: (fieldId: string, value: any) => void;
  updateMultipleFields: (data: Record<string, any>) => void;
  setCurrentStep: (step: number) => void;
  setSubmitting: (isSubmitting: boolean) => void;
  setError: (error: string | null) => void;
  setSuccess: (success: boolean) => void;
  markFieldEncrypted: (fieldId: string) => void;
  resetForm: () => void;
  loadDraft: (draft: any) => void;
}

export const useFormStore = create<FormStoreState>((set) => ({
  currentFormSchema: null,
  currentIMO: null,
  currentCarrier: null,
  formData: {},
  encryptedFields: [],
  currentStep: 0,
  isSubmitting: false,
  error: null,
  success: false,

  setFormSchema: (schema) =>
    set((state) => ({
      currentFormSchema: schema,
      // Auto-populate carriers from schema
      ...(!state.currentCarrier && schema.carriers.length > 0 && {
        currentCarrier: schema.carriers[0],
      }),
    })),

  setIMO: (imo) => set({ currentIMO: imo }),

  setCarrier: (carrier) => set({ currentCarrier: carrier }),

  updateFormData: (fieldId, value) =>
    set((state) => ({
      formData: {
        ...state.formData,
        [fieldId]: value,
      },
    })),

  updateMultipleFields: (data) =>
    set((state) => ({
      formData: {
        ...state.formData,
        ...data,
      },
    })),

  setCurrentStep: (step) => set({ currentStep: step }),

  setSubmitting: (isSubmitting) => set({ isSubmitting }),

  setError: (error) => set({ error }),

  setSuccess: (success) => set({ success }),

  markFieldEncrypted: (fieldId) =>
    set((state) => {
      if (!state.encryptedFields.includes(fieldId)) {
        return {
          encryptedFields: [...state.encryptedFields, fieldId],
        };
      }
      return state;
    }),

  resetForm: () =>
    set({
      currentFormSchema: null,
      currentIMO: null,
      currentCarrier: null,
      formData: {},
      encryptedFields: [],
      currentStep: 0,
      isSubmitting: false,
      error: null,
      success: false,
    }),

  loadDraft: (draft) =>
    set({
      currentFormSchema: draft.schema,
      currentIMO: draft.imo,
      currentCarrier: draft.carrier,
      formData: draft.data,
      encryptedFields: draft.encryptedFields,
      currentStep: draft.currentStep || 0,
    }),
}));
